import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  ReplyDisplayNameResponse,
  UpdateReplyDisplayNameRequest,
} from "@/types/generated/cms.generated";

/**
 * Per-organization "Reply display name" — the name an organization's replies to
 * employee feedback are signed with. Backed by the CMS API
 * `GET/PUT /organizations/{organizationId}/reply-display-name`.
 *
 * The internal Athlyn team manages ANY organization here, so the org id comes
 * from the ROUTE/selected-org (not a tenant context). Blank clears the setting
 * and the app falls back to the organization's registered company name.
 */

/** Max length mirrored from the backend rule (Q3): trimmed, ≤ 50 characters. */
export const REPLY_DISPLAY_NAME_MAX_LENGTH = 50;

const replyDisplayNameKey = (organizationId: string | null | undefined) =>
  ["organizations", organizationId, "reply-display-name"] as const;

// Fetch the current reply display name for the selected organization
export function useOrgReplyDisplayName(
  organizationId: string | null | undefined,
) {
  return useQuery<ReplyDisplayNameResponse>({
    queryKey: replyDisplayNameKey(organizationId),
    queryFn: () =>
      apiRequest("GET", `/organizations/${organizationId}/reply-display-name`),
    enabled: !!organizationId,
  });
}

// Update (or clear) the reply display name for the selected organization.
// Trims client-side; a blank/whitespace value is sent as null so the backend
// stores NULL and the app reverts to the registered company name.
export function useUpdateOrgReplyDisplayName(
  organizationId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateReplyDisplayNameRequest) => {
      const trimmed = data.replyDisplayName?.trim();
      const payload: UpdateReplyDisplayNameRequest = {
        replyDisplayName: trimmed ? trimmed : null,
      };
      return apiRequest<ReplyDisplayNameResponse>(
        "PUT",
        `/organizations/${organizationId}/reply-display-name`,
        payload,
      );
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(replyDisplayNameKey(organizationId), updated);
      queryClient.invalidateQueries({
        queryKey: replyDisplayNameKey(organizationId),
      });
      toast({
        title: "Reply display name updated",
        description: updated.replyDisplayName
          ? `Replies will be signed as "${updated.replyDisplayName}".`
          : "Cleared — replies will use the registered company name.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error?.message ||
          "Failed to update the reply display name. Please try again.",
        variant: "destructive",
      });
    },
  });
}
